import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getAdminSettings, updateAdminSettings } from '../lib/api';
import {
  coerceBlacklistEntries,
  formatBlacklistPhone,
  normalizeBlacklistPhone,
} from '../lib/waPhoneBlacklist';
import { toast } from 'sonner';
import { Ban, Plus, X } from 'lucide-react';

export default function WaPhoneBlacklistCard() {
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blacklistInput, setBlacklistInput] = useState('');
  const [blacklistNameInput, setBlacklistNameInput] = useState('');

  const loadBlacklist = useCallback(async () => {
    try {
      const data = await getAdminSettings();
      setSettingsSnapshot(data);
      setBlacklist(coerceBlacklistEntries(data.wa_phone_blacklist));
    } catch {
      toast.error('No se pudo cargar la lista negra');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBlacklist();
  }, [loadBlacklist]);

  const persistBlacklist = async (nextList) => {
    if (!settingsSnapshot) {
      toast.error('Configuración aún no cargada');
      return null;
    }
    const payload = {
      ...settingsSnapshot,
      appointment_reminder_minutes_before: Number(settingsSnapshot.appointment_reminder_minutes_before),
      appointment_reminder_check_interval_sec: Number(
        settingsSnapshot.appointment_reminder_check_interval_sec,
      ),
      appointment_daily_reminder_time: (settingsSnapshot.appointment_daily_reminder_time || '').trim(),
      wa_phone_blacklist: nextList,
    };
    const saved = await updateAdminSettings(payload);
    setSettingsSnapshot(saved);
    const normalized = coerceBlacklistEntries(saved.wa_phone_blacklist);
    setBlacklist(normalized);
    return saved;
  };

  const handleAddBlacklistPhone = async () => {
    const norm = normalizeBlacklistPhone(blacklistInput);
    if (!norm) {
      toast.error('Escribe un número válido (10 dígitos, con o sin +52)');
      return;
    }
    if (blacklist.some((e) => e.phone === norm)) {
      toast.message('Ese número ya está en la lista negra');
      return;
    }
    const name = String(blacklistNameInput || '').trim().slice(0, 80);
    try {
      await persistBlacklist([...blacklist, { phone: norm, name }]);
      setBlacklistInput('');
      setBlacklistNameInput('');
      toast.success('Número bloqueado y guardado');
    } catch {
      toast.error('No se pudo guardar la lista negra');
    }
  };

  const handleRemoveBlacklistPhone = async (phone) => {
    try {
      await persistBlacklist(blacklist.filter((e) => e.phone !== phone));
      toast.success('Número quitado de la lista negra');
    } catch {
      toast.error('No se pudo actualizar la lista negra');
    }
  };

  return (
    <Card className="mt-8 max-w-xl border border-border" data-testid="wa-phone-blacklist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Ban className="w-5 h-5 text-primary" />
          Lista negra WhatsApp
        </CardTitle>
        <CardDescription>
          Números a los que el bot <strong>no responderá</strong> (mensajes entrantes ignorados). Útil para spam o
          contactos que no quieres atender por el árbol automático. Se guardan los últimos 10 dígitos (México). Al
          pulsar <strong>Añadir</strong> o quitar un número, se guarda al instante en el servidor.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando lista negra…</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[160px] space-y-2">
                <Label htmlFor="wa-blacklist-name">Nombre o nota (opcional)</Label>
                <Input
                  id="wa-blacklist-name"
                  type="text"
                  placeholder="Ej. Spam, proveedor X"
                  value={blacklistNameInput}
                  onChange={(e) => setBlacklistNameInput(e.target.value)}
                  maxLength={80}
                  className="h-11"
                />
              </div>
              <div className="flex-1 min-w-[200px] space-y-2">
                <Label htmlFor="wa-blacklist-input">Teléfono a bloquear</Label>
                <Input
                  id="wa-blacklist-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="771 234 5678 o 527712345678"
                  value={blacklistInput}
                  onChange={(e) => setBlacklistInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddBlacklistPhone();
                    }
                  }}
                  className="h-11"
                />
              </div>
              <Button type="button" variant="outline" className="gap-2" onClick={() => void handleAddBlacklistPhone()}>
                <Plus className="w-4 h-4" />
                Añadir
              </Button>
            </div>
            {blacklist.length > 0 ? (
              <ul className="space-y-2">
                {blacklist.map((entry) => (
                  <li
                    key={entry.phone}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 bg-muted/30"
                  >
                    <div className="min-w-0">
                      {entry.name ? (
                        <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                      ) : null}
                      <p
                        className={`font-mono text-sm ${entry.name ? 'text-muted-foreground' : 'text-foreground'}`}
                      >
                        {formatBlacklistPhone(entry.phone)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive shrink-0"
                      onClick={() => void handleRemoveBlacklistPhone(entry.phone)}
                      aria-label={`Quitar ${entry.name || entry.phone}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No hay números bloqueados.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
